import { useEffect, useMemo, useState } from 'react'
import { clearPostAuthRedirect } from '@/lib/post-auth-redirect'
import { Link, Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Menu,
  ArrowLeft,
  Bell,
  LayoutDashboard,
  GraduationCap,
  Users,
  History,
  ChevronDown,
  LogOut,
  User,
  UserCheck,
  Mail,
  Building2,
  CalendarDays,
  ReceiptText,
  FileText,
  FileCheck2,
  UserPlus,
  ArrowLeftRight,
  Sparkles,
  BookOpen,
  Wallet,
  ShieldCheck,
  FileLock2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '../components/ui/button'
import { useBreadcrumbTrail } from '../components/common/useBreadcrumbTrail'
import { PageSectionContext, type PageSection } from '../components/common/PageSectionContext'
import { ThemeToggle } from '../components/common/ThemeToggle'
import { Sheet, SheetTrigger, SheetContent } from '../components/ui/sheet'
import { useAuthStore, selectRole, selectUser, selectIsSuperAdmin } from '../stores/authStore'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { BrandLogo } from '@/components/common/BrandLogo'
import { useLogout } from '../features/accounts/hooks'
import { notifyApiError } from '../lib/error-handler'
import { toast } from 'sonner'
import { getAvatarAsset, ROLE_LABELS } from '@/features/accounts/utils'
import { RouteErrorBoundary } from '../components/common/ErrorBoundary'
import { SystemAdminAlert } from '../components/common/SystemAdminAlert'
import { TeacherSidebarClasses } from '@/features/dashboard/components/TeacherSidebarClasses'
import { cn } from '@/lib/utils'
import { isSchoolClassesTab, resolveClassesNavTarget } from './classesNav'
import {
  readBackTarget,
  usePageBackStore,
  type PageBackTarget,
} from '../components/common/pageBack'
import { AccessReasonDialog } from '@/features/ferpa/components/AccessReasonDialog'
import { SupportAccessBanner } from '@/features/ferpa/components/SupportAccessBanner'
import type { UserRole } from '../stores/authStore'
type NavChild = {
  to: string
  label: string
  icon?: LucideIcon
}

type NavItem = {
  to?: string
  label: string
  icon: LucideIcon
  children?: NavChild[]
  /** Extra path prefixes that keep this item highlighted (e.g. class detail pages). */
  alsoActiveFor?: string[]
  /** Short-lived wizard / setup shortcuts — styled below primary nav (amber). */
  variant?: 'default' | 'contextual'
}

type NavSection = {
  label: string
  items: NavItem[]
}

const NAV_LINK_BASE =
  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-colors duration-150 [&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:shrink-0'
const NAV_LINK_ACTIVE = 'bg-primary text-primary-foreground shadow-sm'
const NAV_LINK_INACTIVE =
  'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground'
const NAV_CHILD_CLASS =
  'flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] text-sidebar-foreground/65 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground'
const NAV_CHILD_ACTIVE = 'bg-sidebar-accent font-medium text-sidebar-foreground'

/** Wizard & contextual links: secondary gold, compact, indented — distinct from primary nav. */
function contextualNavClass({
  isActive,
  routeActive,
}: {
  isActive: boolean
  routeActive: boolean
}) {
  const on = isActive || routeActive
  return cn(
    'ml-2 flex items-center gap-2 rounded-md border py-1.5 pr-2 pl-2 font-medium text-[13px] leading-snug transition-colors',
    'border-amber-500/35 bg-amber-500/[0.08] text-amber-950 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100',
    on
      ? 'border-amber-600/50 bg-amber-500/18 shadow-sm ring-1 ring-amber-500/25 dark:bg-amber-500/18 dark:ring-amber-400/20'
      : 'hover:bg-amber-500/12 dark:hover:bg-amber-500/14',
  )
}

type ActiveLocation = {
  pathname: string
  search: string
}

const isPathActive = (location: ActiveLocation, target?: string) => {
  if (!target) return false

  const { pathname, search } = location
  const [targetPath, targetQuery] = target.split('?')

  if (targetQuery) {
    if (pathname !== targetPath) return false

    const targetParams = new URLSearchParams(targetQuery)
    const currentParams = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

    for (const [key, value] of targetParams.entries()) {
      if (!currentParams.getAll(key).includes(value)) {
        return false
      }
    }

    return true
  }

  // The school hub's Classes tab has its own sidebar entry, so "Schools" stays quiet there.
  if (targetPath === '/schools' && isSchoolClassesTab(location)) return false

  if (pathname === targetPath) return true
  const base = targetPath.endsWith('/') ? targetPath : `${targetPath}/`
  return pathname.startsWith(base)
}

const buildSections = (
  role: UserRole | null,
  isSuperAdmin: boolean,
  pathname?: string,
  classesTo: string = '/classes',
): NavSection[] => {
  if (role === 'TEACHER') {
    // Classes are the teacher's work surface but they're year-scoped, not
    // top-level destinations. The dashboard is the canonical class launcher.
    return [
      {
        label: 'Overview',
        items: [{ label: 'Dashboard', to: '/dashboard/teacher', icon: LayoutDashboard }],
      },
    ]
  }

  if (role === 'PARENT') {
    return [
      {
        label: 'Overview',
        items: [{ label: 'Dashboard', to: '/dashboard/parent', icon: LayoutDashboard }],
      },
      {
        label: 'Family',
        items: [
          { label: 'Registrations', to: '/registrations', icon: UserPlus },
          { label: 'Payments', to: '/payments', icon: Wallet },
        ],
      },
      {
        label: 'Account',
        items: [{ label: 'Profile', to: '/profile', icon: User }],
      },
      {
        label: 'Family',
        items: [
          {
            label: 'Privacy & records',
            to: '/privacy',
            icon: ShieldCheck,
          },
        ],
      },
    ]
  }

  const canManageRegistration = role === 'SYSTEM_ADMIN' || role === 'SCHOOL_ADMIN' || isSuperAdmin

  const base: NavSection[] = [
    {
      label: 'Overview',
      items: [{ label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard }],
    },
    {
      label: 'Academics',
      items: [
        { label: 'Schools', to: '/schools', icon: Building2 },
        ...(role === 'SYSTEM_ADMIN' || role === 'SCHOOL_ADMIN' || isSuperAdmin
          ? [
              {
                label: 'Classes',
                to: classesTo,
                icon: BookOpen,
                alsoActiveFor: ['/academics/class/'],
              },
            ]
          : []),
      ],
    },
    {
      label: 'Payments',
      items: [
        { label: 'Payments', to: '/payments/feed', icon: ReceiptText },
        { label: 'Families', to: '/payments/customers', icon: Users },
        { label: 'Needs attention', to: '/payments/follow-up', icon: Bell },
      ],
    },
  ]

  if (canManageRegistration) {
    base.splice(2, 0, {
      label: 'Registrations',
      items: [{ label: 'Registration Mgmt', to: '/registration-management', icon: FileText }],
    })
  } else {
    base[1].items.push({ label: 'Registrations', to: '/registrations', icon: UserPlus })
  }

  if (role === 'SYSTEM_ADMIN' || role === 'SCHOOL_ADMIN' || isSuperAdmin) {
    base.splice(1, 0, {
      label: 'People',
      items: [
        { label: 'Students', to: '/students', icon: GraduationCap },
        { label: 'Document review', to: '/documents/review', icon: FileCheck2 },
        { label: 'Directory', to: '/users', icon: UserCheck },
        { label: 'Invitations', to: '/invitations', icon: Mail },
      ],
    })

    base.splice(base.length - 1, 0, {
      label: 'Logs',
      items: [{ label: 'Login History', to: '/login-history', icon: History }],
    })

    base.splice(base.length - 1, 0, {
      label: 'Privacy & FERPA',
      items: [
        ...(isSuperAdmin
          ? [
              {
                label: 'Access Audit',
                to: '/privacy/audit',
                icon: ShieldCheck,
                alsoActiveFor: ['/privacy/students/', '/privacy/actors/'],
              },
            ]
          : []),
        { label: 'School Privacy', to: '/privacy/school', icon: FileLock2 },
      ],
    })
  }

  const schoolCreateWizardMatch = pathname === '/schools/new'
  const schoolSetupMatch = pathname?.match(/^\/schools\/([^/]+)\/setup$/)
  const academicYearWizardMatch = pathname?.match(/^\/schools\/([^/]+)\/academic-year\/new$/)
  const rolloverWizardMatch = pathname?.match(/^\/schools\/([^/]+)\/rollover$/)
  const academicsIndex = base.findIndex(section => section.label === 'Academics')
  if (academicsIndex >= 0) {
    const contextual: NavItem[] = []
    if (schoolCreateWizardMatch) {
      contextual.push({
        label: 'Create school',
        to: '/schools/new',
        icon: Building2,
        variant: 'contextual',
      })
    }
    if (schoolSetupMatch) {
      contextual.push({
        label: 'School Setup',
        to: `/schools/${schoolSetupMatch[1]}/setup`,
        icon: Building2,
        variant: 'contextual',
      })
    }
    if (academicYearWizardMatch) {
      contextual.push({
        label: 'New academic year',
        to: `/schools/${academicYearWizardMatch[1]}/academic-year/new`,
        icon: CalendarDays,
        variant: 'contextual',
      })
    }
    if (rolloverWizardMatch) {
      contextual.push({
        label: 'Year-end rollover',
        to: `/schools/${rolloverWizardMatch[1]}/rollover`,
        icon: ArrowLeftRight,
        variant: 'contextual',
      })
    }
    if (contextual.length) {
      base[academicsIndex] = {
        ...base[academicsIndex],
        items: [...base[academicsIndex].items, ...contextual],
      }
    }
  }

  return base
}

type OpenState = Record<string, boolean>

const getItemKey = (item: NavItem) => item.to ?? item.label

function useNavOpenState(sections: NavSection[], location: ActiveLocation) {
  const compute = (loc: ActiveLocation) => {
    const initial: OpenState = {}
    sections.forEach(section => {
      section.items.forEach(item => {
        if (item.children?.length) {
          const key = getItemKey(item)
          const shouldOpen =
            isPathActive(loc, item.to) || item.children.some(child => isPathActive(loc, child.to))
          initial[key] = shouldOpen
        }
      })
    })
    return initial
  }

  const [openMap, setOpenMap] = useState<OpenState>(() => compute(location))

  useEffect(() => {
    const currentLocation: ActiveLocation = {
      pathname: location.pathname,
      search: location.search,
    }
    setOpenMap(prev => {
      let changed = false
      const next = { ...prev }
      sections.forEach(section => {
        section.items.forEach(item => {
          if (!item.children?.length) return
          const key = getItemKey(item)
          const shouldOpen =
            isPathActive(currentLocation, item.to) ||
            item.children.some(child => isPathActive(currentLocation, child.to))
          if (shouldOpen && !next[key]) {
            next[key] = true
            changed = true
          }
        })
      })
      return changed ? next : prev
    })
  }, [location.pathname, location.search, sections])

  return [openMap, setOpenMap] as const
}

/** Sidebar destination owning the current route: longest matching link wins. */
function findActiveSection(sections: NavSection[], pathname: string): PageSection | null {
  let best: { section: PageSection; length: number } | null = null
  const consider = (to: string | undefined, label: string, icon: LucideIcon) => {
    if (!to) return
    const path = to.split('?')[0]
    const matches = pathname === path || pathname.startsWith(`${path}/`)
    if (matches && (!best || path.length > best.length)) {
      best = { section: { label, icon }, length: path.length }
    }
  }
  for (const section of sections) {
    for (const item of section.items) {
      consider(item.to, item.label, item.icon)
      for (const prefix of item.alsoActiveFor ?? [])
        consider(prefix.replace(/\/$/, ''), item.label, item.icon)
      for (const child of item.children ?? []) {
        consider(child.to, child.label, child.icon ?? item.icon)
      }
    }
  }
  return (best as { section: PageSection } | null)?.section ?? null
}

interface NavListProps {
  sections: NavSection[]
}

function NavList({ sections }: NavListProps) {
  const location = useLocation()
  const activeLocation: ActiveLocation = {
    pathname: location.pathname,
    search: location.search,
  }
  const [openMap, setOpenMap] = useNavOpenState(sections, activeLocation)

  const toggleItem = (item: NavItem) => {
    const key = getItemKey(item)
    setOpenMap(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div className='space-y-5'>
      {sections.map(section => (
        <div key={section.label} className='space-y-1'>
          <div className='px-3 pb-1 font-medium text-[11px] text-muted-foreground/80 uppercase tracking-[0.08em]'>
            {section.label}
          </div>
          <nav className='space-y-0.5'>
            {section.items.map(item => {
              const Icon = item.icon
              const hasChildren = !!item.children?.length
              const key = getItemKey(item)
              const active =
                isPathActive(activeLocation, item.to) ||
                item.alsoActiveFor?.some(prefix => location.pathname.startsWith(prefix)) ||
                (item.label === 'Classes' && isSchoolClassesTab(activeLocation)) ||
                item.children?.some(child => isPathActive(activeLocation, child.to))
              const open = hasChildren ? (openMap[key] ?? false) : false

              // `active` already honours query strings (the router's own isActive ignores
              // them, which would light up every hub tab's entry at once).
              const linkClass = () =>
                `${NAV_LINK_BASE} ${active ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE} ${
                  hasChildren ? 'pr-8' : ''
                }`

              return (
                <div key={key} className='relative'>
                  {hasChildren ? (
                    <button
                      type='button'
                      onClick={() => toggleItem(item)}
                      className={`${NAV_LINK_BASE} ${
                        active ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE
                      } relative w-full justify-start text-left ${hasChildren ? 'pr-10' : ''}`}
                      aria-label={`${open ? 'Collapse' : 'Expand'} ${item.label}`}
                      aria-expanded={open}
                    >
                      <Icon />
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`!h-4 !w-4 pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`}
                      />
                    </button>
                  ) : item.to ? (
                    item.variant === 'contextual' ? (
                      <NavLink
                        to={item.to}
                        title='Guided flow — you can return anytime from the school hub'
                        className={({ isActive }) =>
                          contextualNavClass({
                            isActive,
                            routeActive: isPathActive(activeLocation, item.to),
                          })
                        }
                      >
                        <Sparkles
                          className='h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400'
                          aria-hidden
                        />
                        <Icon className='h-4 w-4 shrink-0' />
                        <span className='min-w-0'>{item.label}</span>
                        <span className='sr-only'> (wizard)</span>
                      </NavLink>
                    ) : (
                      <NavLink to={item.to} className={linkClass}>
                        <Icon />
                        <span>{item.label}</span>
                      </NavLink>
                    )
                  ) : (
                    <div className={`${NAV_LINK_BASE} ${NAV_LINK_INACTIVE}`}>
                      <Icon />
                      <span>{item.label}</span>
                    </div>
                  )}
                  {hasChildren && open && item.children ? (
                    <div className='mt-0.5 mb-1 ml-[21px] space-y-0.5 border-sidebar-border border-l pl-2.5'>
                      {item.children.map(child => {
                        const ChildIcon = child.icon || User
                        return (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            className={({ isActive }) =>
                              `${NAV_CHILD_CLASS} ${
                                isPathActive(activeLocation, child.to) || isActive
                                  ? NAV_CHILD_ACTIVE
                                  : ''
                              }`
                            }
                          >
                            <ChildIcon className='h-3.5 w-3.5 shrink-0 opacity-80' />
                            <span>{child.label}</span>
                          </NavLink>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </nav>
        </div>
      ))}
    </div>
  )
}

function Sidebar({
  sections,
  showTeacherClasses,
}: {
  sections: NavSection[]
  showTeacherClasses?: boolean
}) {
  return (
    <aside className='hidden w-64 shrink-0 flex-col border-sidebar-border border-r bg-sidebar md:flex print:hidden'>
      <div className='flex h-16 shrink-0 items-center border-sidebar-border border-b px-5'>
        <Link
          to='/dashboard'
          aria-label='Maktab home'
          className='rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/45'
        >
          <BrandLogo variant='header' className='h-8' />
        </Link>
      </div>
      <div className='min-h-0 flex-1 overflow-y-auto px-3 pt-4 pb-6'>
        <NavList sections={sections} />
        {showTeacherClasses ? (
          <div className='mt-5 border-sidebar-border border-t pt-5'>
            <TeacherSidebarClasses />
          </div>
        ) : null}
      </div>
    </aside>
  )
}

function MobileNav({
  sections,
  showTeacherClasses,
}: {
  sections: NavSection[]
  showTeacherClasses?: boolean
}) {
  return (
    <nav className='flex flex-col' role='navigation' aria-label='Main'>
      <div className='mb-6 px-2'>
        <BrandLogo variant='header' className='h-8' />
      </div>
      <NavList sections={sections} />
      {showTeacherClasses ? (
        <div className='mt-4 border-sidebar-border border-t pt-4'>
          <TeacherSidebarClasses />
        </div>
      ) : null}
    </nav>
  )
}

/**
 * Where a link said this page was opened from (router state `backTo`). Kept while the
 * user stays on the same path: tab and filter changes rewrite the URL and drop the state.
 */
function useCameFrom() {
  const location = useLocation()
  const fromState = readBackTarget(location.state)
  const [remembered, setRemembered] = useState<{ path: string; target: PageBackTarget } | null>(
    null,
  )
  const label = fromState?.label
  const to = fromState?.to
  useEffect(() => {
    if (label && to) setRemembered({ path: location.pathname, target: { label, to } })
  }, [label, to, location.pathname])
  return fromState ?? (remembered?.path === location.pathname ? remembered.target : null)
}

/** Left side of the top bar: where you are, and a clear way back from detail pages. */
function TopBarLocation({ section }: { section: PageSection | null }) {
  const trail = useBreadcrumbTrail()
  const current = trail[trail.length - 1]
  const backOverride = usePageBackStore(s => s.target)
  // Only detail pages (below a top-level destination) get a back button. In order: the
  // page the user came from (a link passed it), the page's own choice (e.g. the school's
  // Classes tab), then the nearest parent in the URL that is a page this user can open.
  // Dashboard is always one, so a detail page never loses its way back.
  const isDetailPage = trail.length > 2
  const trailParent = isDetailPage
    ? trail
        .slice(0, -1)
        .reverse()
        .find(crumb => crumb.linkable)
    : undefined
  const cameFrom = useCameFrom()
  const parent = isDetailPage ? (cameFrom ?? backOverride ?? trailParent) : undefined

  if (parent) {
    return (
      <div className='flex min-w-0 items-center gap-3'>
        <Button
          asChild
          variant='outline'
          size='sm'
          className='h-9 shrink-0 rounded-full bg-background pr-4 pl-3 text-sm'
        >
          <Link to={parent.to}>
            <ArrowLeft />
            <span>
              <span className='hidden sm:inline'>Back to </span>
              {parent.label}
            </span>
          </Link>
        </Button>
        <span className='hidden h-5 w-px shrink-0 bg-border md:block' aria-hidden='true' />
        <span className='hidden truncate font-semibold text-[15px] md:block' aria-current='page'>
          {current?.label}
        </span>
      </div>
    )
  }

  // Top-level pages carry their own titled header, so the bar shows today's date instead.
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
  return (
    <div className='flex min-w-0 items-center gap-2.5 text-muted-foreground'>
      <CalendarDays className='h-[18px] w-[18px] shrink-0' aria-hidden />
      <span className='truncate font-medium text-[15px]'>{today}</span>
      <span className='sr-only'>, current page: {section?.label ?? current?.label}</span>
    </div>
  )
}

function TopBar({
  sections,
  section,
  showTeacherClasses,
}: {
  sections: NavSection[]
  section: PageSection | null
  showTeacherClasses?: boolean
}) {
  const navigate = useNavigate()
  const user = useAuthStore(selectUser)
  const role = useAuthStore(selectRole)
  const logoutMutation = useLogout()

  const avatarAsset = useMemo(
    () => getAvatarAsset(user?.profile?.avatar_key),
    [user?.profile?.avatar_key],
  )

  const initials = useMemo(() => {
    const source = user?.profile?.full_name || user?.email || ''
    if (!source) return 'U'
    return (
      source
        .split(' ')
        .filter(Boolean)
        .map(part => part[0]?.toUpperCase())
        .join('')
        .slice(0, 2) || 'U'
    )
  }, [user])

  const displayName = user?.profile?.full_name || user?.email || 'Account'
  const roleLabel = formatRoleLabel(role)
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  // Close the mobile nav sheet whenever navigation happens from inside it.
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, location.search])

  const handleProfile = () => navigate('/profile')

  const handleLogout = () => {
    if (logoutMutation.isPending) return
    // A page remembered before this sign-out must not open for the next person.
    clearPostAuthRedirect()
    logoutMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success('Signed out')
        navigate('/login')
      },
      onError: error => notifyApiError(error, 'Logout failed'),
    })
  }

  return (
    <header className='flex h-16 shrink-0 items-center justify-between gap-4 border-border/70 border-b bg-card px-4 sm:px-6 lg:px-8 print:hidden'>
      <div className='flex min-w-0 flex-1 items-center gap-3'>
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetTrigger asChild>
            <Button size='icon' variant='ghost' className='-ml-2 md:hidden' aria-label='Open menu'>
              <Menu className='h-5 w-5' />
            </Button>
          </SheetTrigger>
          <SheetContent side='left' className='overflow-y-auto bg-sidebar p-4 sm:max-w-xs'>
            <MobileNav sections={sections} showTeacherClasses={showTeacherClasses} />
          </SheetContent>
        </Sheet>
        <TopBarLocation section={section} />
      </div>
      <div className='flex shrink-0 items-center gap-1.5'>
        <ThemeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type='button'
              className='group flex items-center gap-2.5 rounded-full py-1 pr-1 pl-1 transition-colors hover:bg-foreground/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/45 data-[state=open]:bg-foreground/[0.05] lg:pr-3'
            >
              <Avatar className='h-8 w-8 ring-1 ring-border/70'>
                <AvatarImage src={avatarAsset.src} alt={avatarAsset.label} />
                <AvatarFallback className='bg-accent font-medium text-accent-foreground text-xs'>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className='hidden min-w-0 flex-col items-start text-left lg:flex'>
                <span className='max-w-[12rem] truncate font-medium text-sm leading-tight'>
                  {displayName}
                </span>
                {roleLabel ? (
                  <span className='text-muted-foreground text-xs leading-tight'>{roleLabel}</span>
                ) : null}
              </span>
              <ChevronDown className='hidden h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180 lg:block' />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[calc(100vw-1rem)] max-w-[16rem] p-2 sm:w-64'
            align='end'
            sideOffset={8}
          >
            <DropdownMenuLabel className='p-3'>
              <div className='flex flex-col space-y-2'>
                <div className='flex items-center gap-3'>
                  <Avatar className='h-10 w-10 ring-1 ring-border/70'>
                    <AvatarImage src={avatarAsset.src} alt={avatarAsset.label} />
                    <AvatarFallback className='bg-accent font-medium text-accent-foreground'>
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className='flex min-w-0 flex-1 flex-col'>
                    <span className='truncate font-medium text-sm'>{displayName}</span>
                    {user?.email ? (
                      <span className='truncate text-muted-foreground text-xs'>{user.email}</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup className='p-1'>
              <DropdownMenuItem onSelect={handleProfile} className='cursor-pointer'>
                <User className='mr-1 h-4 w-4' />
                <span className='font-medium'>Profile</span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup className='p-1'>
              <DropdownMenuItem
                disabled={logoutMutation.isPending}
                onSelect={handleLogout}
                className='cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive'
              >
                <LogOut className='mr-1 h-4 w-4' />
                <span className='font-medium'>
                  {logoutMutation.isPending ? 'Signing out...' : 'Log out'}
                </span>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

function formatRoleLabel(role: UserRole | null | undefined): string | null {
  if (!role) return null
  return ROLE_LABELS[role] ?? null
}

export function DashboardLayout() {
  const role = useAuthStore(selectRole)
  const isSuperAdmin = useAuthStore(selectIsSuperAdmin)
  const location = useLocation()
  const user = useAuthStore(selectUser)
  const schoolKey = (user?.school_assignments ?? [])
    .filter(a => a.is_active)
    .map(a => a.school_id)
    .join(',')
  const classesTo = useMemo(
    () => resolveClassesNavTarget(role, schoolKey ? schoolKey.split(',') : []),
    [role, schoolKey],
  )
  const sections = useMemo(
    () => buildSections(role, isSuperAdmin, location.pathname, classesTo),
    [isSuperAdmin, location.pathname, role, classesTo],
  )
  const showTeacherClasses = role === 'TEACHER'
  const section = useMemo(
    () => findActiveSection(sections, location.pathname),
    [sections, location.pathname],
  )

  return (
    <div className='flex h-full print:block print:h-auto'>
      <Sidebar sections={sections} showTeacherClasses={showTeacherClasses} />
      <div className='flex min-w-0 flex-1 flex-col print:block'>
        <TopBar sections={sections} section={section} showTeacherClasses={showTeacherClasses} />
        <div className='print:hidden'>
          <SupportAccessBanner />
          <SystemAdminAlert />
        </div>
        <AccessReasonDialog />
        <main className='min-h-0 flex-1 overflow-y-auto px-4 pt-5 pb-10 sm:px-6 sm:pt-6 lg:px-8 print:overflow-visible print:p-0'>
          <div className='mx-auto w-full max-w-[1440px] print:max-w-none'>
            <PageSectionContext.Provider value={section}>
              <RouteErrorBoundary area='this page'>
                <Outlet />
              </RouteErrorBoundary>
            </PageSectionContext.Provider>
          </div>
        </main>
      </div>
    </div>
  )
}

export default DashboardLayout
